import React, { useState } from 'react';
import { Nav, Modal, Button } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import {
  FaTachometerAlt,
  FaUsers,
  FaBriefcase,
  FaClock,
  FaUserTie,
  FaClipboardCheck,
  FaSignOutAlt,
  FaChevronLeft,
  FaChevronRight,
  FaBuilding,
  FaExclamationTriangle,
  FaImage
} from 'react-icons/fa';

const Sidebar = () => {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [activeHover, setActiveHover] = useState(null);
  const [adminProfile] = useState({
    name: "Super Admin",
    role: "Administrator",
    status: "online"
  });

  const navLinks = [
    { path: '/', name: 'Dashboard', icon: <FaTachometerAlt /> },
    { path: '/users', name: 'Users', icon: <FaUsers /> },
    { path: '/jobs', name: 'Jobs', icon: <FaBriefcase /> },
    { path: '/applications', name: 'Applications', icon: <FaClock /> },
    { path: '/submissions', name: 'Submissions', icon: <FaClipboardCheck /> },
    { path: '/expertise', name: 'Expertise', icon: <FaUserTie /> },
    { path: '/companies', name: 'Companies', icon: <FaBuilding /> },
    { path: '/posters', name: 'Posters', icon: <FaImage /> },
  ];

  const toggleSidebar = () => {
    setCollapsed(!collapsed);
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    window.location.href = '/login';
  };

  return (
    <>
      <div 
        className={`sidebar-container ${collapsed ? 'collapsed' : ''}`}
      >
        {/* Profile Section */}
        <div className="profile-section">
          <div className="avatar-wrapper">
            <img
              src='https://collegeall.com/img/Logo/collegeallLogo.png'
              alt="admin"
              className={`profile-img ${collapsed ? 'mini' : 'full'}`}
            />
          </div>
          
          {!collapsed && (
            <div className="profile-details animate-fade-in">
              <h6 className="admin-name">{adminProfile.name}</h6>
              <span className="admin-role">{adminProfile.role}</span>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="nav-scroll-area">
          <Nav className="flex-column px-2">
            {!collapsed && (
              <div className="nav-label mb-2 px-3">
                MAIN MENU
              </div>
            )}
            
            {navLinks.map((link, index) => {
              const isActive = location.pathname === link.path;
              return (
                <Nav.Item 
                  key={index} 
                  className="mb-1"
                  onMouseEnter={() => setActiveHover(index)}
                  onMouseLeave={() => setActiveHover(null)}
                >
                  <Link
                    to={link.path}
                    className={`modern-nav-link ${isActive ? 'active' : ''} ${collapsed ? 'justify-content-center' : ''}`}
                  >
                    <div className="nav-icon-box">
                      {link.icon}
                    </div>
                    {!collapsed && <span className="nav-text ms-2">{link.name}</span>}
                    {isActive && !collapsed && <div className="active-glow-indicator" />}
                  </Link>
                </Nav.Item>
              );
            })}
          </Nav>
        </div>

        {/* Footer Actions */}
        <div className="sidebar-footer">
          <div 
            className={`modern-nav-link logout-btn ${collapsed ? 'justify-content-center' : ''}`}
            onClick={() => setShowLogoutConfirm(true)}
          >
            <div className="nav-icon-box text-danger">
              <FaSignOutAlt />
            </div>
            {!collapsed && <span className="ms-2 text-danger fw-semibold">Logout</span>}
          </div>

          <button className="collapse-toggle" onClick={toggleSidebar}>
            {collapsed ? <FaChevronRight size={10} /> : <FaChevronLeft size={10} />}
          </button>
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      <Modal 
        show={showLogoutConfirm} 
        onHide={() => setShowLogoutConfirm(false)} 
        centered 
        size="sm"
        className="logout-confirm-modal"
      >
        <Modal.Body className="p-4 text-center">
          <div className="logout-icon-wrapper mb-3">
            <FaExclamationTriangle size={28} />
          </div>
          <h5 className="fw-bold text-dark mb-2">Sign Out?</h5>
          <p className="text-muted small mb-4">
            Are you sure you want to end your session? You will need to login again to access the dashboard.
          </p>
          <div className="d-grid gap-2">
            <Button 
              variant="danger" 
              className="fw-bold py-2 shadow-sm rounded-3" 
              onClick={handleLogout}
            >
              LOGOUT
            </Button>
            <Button 
              variant="light" 
              className="fw-semibold py-2 text-muted border-0 bg-transparent" 
              onClick={() => setShowLogoutConfirm(false)}
            >
              CANCEL
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <style>{`
        .sidebar-container {
          width: 230px;
          height: 100vh;
          background: #0f172a;
          color: #94a3b8;
          display: flex;
          flex-direction: column;
          position: sticky;
          top: 0;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          z-index: 1000;
          border-right: 1px solid rgba(255, 255, 255, 0.05);
        }

        .sidebar-container.collapsed {
          width: 70px;
        }

        .profile-section {
          padding: 1.5rem 1rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
          margin-bottom: 0.75rem;
        }

        .avatar-wrapper {
          position: relative;
          margin-bottom: 0.75rem;
        }

        .profile-img {
          border-radius: 7px;
          background: #ffff;
          padding: 3px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          transition: all 0.3s ease;
          width: 100%;
        }

        .profile-img.full { width: 100%; }
        .profile-img.mini { width: 100%; }

        .profile-details {
          text-align: center;
        }

        .admin-name {
          color: white;
          font-weight: 600;
          margin-bottom: 0;
          font-size: 0.8rem;
        }

        .admin-role {
          font-size: 0.6rem;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .nav-scroll-area {
          flex-grow: 1;
          overflow-y: auto;
          scrollbar-width: none;
        }

        .nav-label {
          font-size: 0.6rem;
          font-weight: 800;
          color: #475569;
          letter-spacing: 1.5px;
        }

        .modern-nav-link {
          display: flex;
          align-items: center;
          padding: 0.6rem 0.75rem;
          text-decoration: none;
          color: #94a3b8;
          border-radius: 8px;
          margin: 0 0.5rem;
          transition: all 0.2s ease;
          position: relative;
          font-weight: 500;
          font-size: 0.75rem;
        }

        .modern-nav-link:hover {
          background: rgba(255, 255, 255, 0.05);
          color: white;
        }

        .modern-nav-link.active {
          background: rgba(99, 102, 241, 0.15);
          color: #818cf8;
        }

        .modern-nav-link.active .nav-icon-box {
          color: #818cf8;
        }

        .nav-icon-box {
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.9rem;
          transition: transform 0.2s ease;
        }

        .active-glow-indicator {
          position: absolute;
          right: 10px;
          width: 4px;
          height: 4px;
          background: #818cf8;
          border-radius: 50%;
          box-shadow: 0 0 8px #818cf8;
        }

        .sidebar-footer {
          padding: 0.75rem;
          border-top: 1px solid rgba(255, 255, 255, 0.05);
          position: relative;
        }

        .logout-btn {
          margin-bottom: 0.75rem;
          cursor: pointer;
        }

        .logout-btn:hover {
          background: rgba(239, 68, 68, 0.1);
        }

        .collapse-toggle {
          width: 24px;
          height: 24px;
          background: #1e293b;
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: #94a3b8;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .collapse-toggle:hover {
          background: #334155;
          color: white;
        }

        .logout-confirm-modal .modal-content {
          border-radius: 1.25rem;
          border: 0;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
        }

        .logout-icon-wrapper {
          width: 64px;
          height: 64px;
          background: #fff1f2;
          color: #e11d48;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto;
        }

        .animate-fade-in {
          animation: fadeIn 0.2s ease-out forwards;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-3px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .nav-scroll-area::-webkit-scrollbar { display: none; }
      `}</style>
    </>
  );
};

export default Sidebar;